import React from "react";
import StatusChip from "../StatusChip";
import ComboField from "./ComboField";
import useSelectOnFocus from "./selectOnFocus";
import { otherTaskLabel, pickerValue, taskLabel, taskPickerOptions } from "./taskOptions";

const YEARS = Array.from({ length: 11 }, (_, i) => new Date().getFullYear() + 1 - i);

const TYPES = [
  { value: "BILLABLE", label: "Billable" },
  { value: "NON_BILLABLE", label: "Non-bill" },
];

const DATE_FMT = { weekday: "short", month: "short", day: "numeric", year: "numeric" };
const LOCK_FMT = { month: "short", day: "numeric", year: "numeric" };

// Hover text for the red (i) beside a rolled-forward task.
export function rolledForwardTitle(locksAt) {
  const base = "This task has been rolled forward to next year.";
  if (!locksAt) return `${base} Make sure this time belongs to this year's file.`;
  const when = new Date(locksAt).toLocaleDateString("en-US", LOCK_FMT);
  return `${base} Time can still be entered here until ${when}, when it locks.`;
}

// Everything on the line, in column order, as one string for the row's
// tooltip. The columns are narrow enough that most values are cut off, so
// hovering anywhere on a line is how you read the rest of it. Blank fields are
// dropped rather than shown as empty labels -- a half-filled line should read
// as short, not as a list of nothings.
export function rowSummary(entry, clients, tasks, otherTasks) {
  const name = (list, id) => (list || []).find((x) => String(x.id) === String(id))?.name;
  const parts = [];
  if (entry.time_code) parts.push(entry.time_code);
  const client = name(clients, entry.client);
  if (client) parts.push(client);
  if (entry.time_locked && entry.task_name) {
    parts.push(`${entry.task_name} (time locked)`);
  } else if (entry.other_task) {
    const other = (otherTasks || []).find((o) => String(o.id) === String(entry.other_task));
    if (other) parts.push(otherTaskLabel(other));
    else if (entry.task_name) parts.push(`${entry.task_name} (Other)`);
  } else {
    const task = (tasks || []).find((t) => String(t.id) === String(entry.task));
    if (task) parts.push(taskLabel(task));
    else if (entry.task_name) parts.push(entry.task_name);
  }
  if (entry.year_end) parts.push(`YE ${entry.year_end}`);
  if (entry.billable_code) {
    parts.push(entry.billable_code === "NON_BILLABLE" ? "Non-billable" : "Billable");
  }
  const hours = Number(entry.hours);
  if (entry.hours !== "" && entry.hours != null && Number.isFinite(hours) && hours > 0) {
    parts.push(`${hours.toFixed(2)} hrs`);
  }
  if (entry.notes) parts.push(entry.notes);

  // billable_code always has a value, so "nothing typed yet" needs saying.
  const meaningful = parts.filter((p) => p !== "Billable" && p !== "Non-billable");
  const date = new Date(`${entry.date}T00:00:00`).toLocaleDateString("en-US", DATE_FMT);
  return `${date} · ${meaningful.length ? parts.join(" · ") : "empty line"}`;
}

// Time is booked in quarter hours. Arrows step by .25; a typed value is snapped
// on blur so the stored figure is always a clean quarter -- hours feed billing,
// and a stray 1.1 there is a number nobody can reconcile against a timesheet.
export function snapQuarter(v) {
  if (v === "" || v == null) return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return (Math.round(n * 4) / 4).toFixed(2);
}

// One autosaving spreadsheet line. Editable at every stage; editing an approved
// or rejected entry re-queues it for approval (handled server-side).
// In `review` mode the trailing column offers "Send back" instead of delete.
//
// Every picker is a ComboField rather than a native <select> so the whole row
// obeys one rule -- arrow to highlight, Tab to commit and move on. A native
// select cannot do that for the client (1000+ rows, needs type-to-filter), and
// a row with two different interaction models is worse than one with either.
export default function EntryRow({
  entry, clients, timeCodes, tasks, otherTasks, editable: editableProp, review, saveStatus,
  onChange, onDelete, onSendBack, onCreateTask,
}) {
  // A shut year (or a completed Other task) is a closed file: the server
  // rejects any write to this row (TimeEntrySerializer.validate). Freezing it
  // here means the rule is visible rather than discovered on save. The row
  // stays fully readable -- hiding booked time would silently change
  // historical WIP. A completed Task alone no longer locks (2026-10-27): late
  // time can go on a finished file until its year locks.
  const locked = !!entry.time_locked;
  const editable = editableProp && !locked;

  // WeekView fetches a client's tasks only once that client is picked, so
  // `tasks` is undefined for the width of that request. Rendering it as an
  // empty list would read as "this client has no tasks".
  const tasksLoading = !!entry.client && tasks === undefined;

  // A task IS a fiscal year's worth of work, so once the line names one there
  // is nothing left to decide here: the cell stops being a field to fill in
  // and becomes a value to read.
  //
  // Read off the LINE, never off the picker's list. Asking the list whether it
  // holds this task gave three separate ways to end up with an ordinary
  // dropdown sitting in the Tab path: the list not fetched yet (every row, for
  // the width of that request), the task filtered out of it by year_open (a
  // closed year), and a task carrying no fiscal_year of its own. The line
  // naming a task is the fact that matters; the list only supplies the name
  // for the tooltip.
  const pickedTask = entry.task
    ? (tasks || []).find((t) => String(t.id) === String(entry.task))
    : null;
  const yearLocked = !!entry.task;

  // The red (i): this line's task has been rolled forward to next year. Time
  // still goes on it until the year locks; the mark says so, so nobody books
  // next year's work to last year's file by accident.
  const rolled = !!entry.task && (
    !!entry.task_rolled_forward || pickedTask?.next_year?.state === "rolled");
  const rolledTitle = rolledForwardTitle(entry.task_locks_at);

  // The picker lists only what is bookable; a line already on a completed
  // task that dropped out of that list still has to show its task.
  const options = taskPickerOptions(tasks, otherTasks);
  if (entry.task && !pickedTask && entry.task_name) {
    options.unshift({ value: `t:${entry.task}`, label: entry.task_name });
  }

  const selectHours = useSelectOnFocus();
  const onHoursBlur = (e) => {
    selectHours.onBlur();
    const snapped = snapQuarter(e.target.value);
    if (snapped !== String(entry.hours ?? "")) onChange("hours", snapped);
  };

  return (
    <tr className="te-row" title={rowSummary(entry, clients, tasks, otherTasks)}>
      <td className="te-cell">
        <ComboField ariaLabel="Code" disabled={!editable} value={entry.time_code || ""}
          options={timeCodes.map((t) => ({ value: t.code, label: t.code }))}
          onChange={(v) => onChange("time_code", v)} />
      </td>
      <td className="te-cell">
        <ComboField ariaLabel="Client" disabled={!editable} value={entry.client}
          options={clients.map((c) => ({ value: c.id, label: c.name }))}
          onChange={(v) => onChange("client", v)} />
      </td>
      <td className="te-cell">
        {locked && entry.task_name ? (
          // A locked line's task may not be in the picker's list, so show the
          // name rather than an empty field.
          <div className="te-notes" title={`${entry.task_name} — time locked`}>
            🔒 {entry.task_name}
          </div>
        ) : (
          // Deliberately NOT disabled on "no client yet". Tab's destination is
          // worked out from the DOM as it stands during the keystroke, so a
          // Task field that only becomes enabled on the next React render is
          // skipped by the very Tab that picked the client -- focus jumps
          // straight to Year End. With no client the list is simply empty.
          // The create row is offered only once the line names a client: a
          // task hangs off one, so there is nothing to create before then.
          // Saying so beats leaving "No match" as the answer.
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <ComboField ariaLabel="Task" disabled={!editable}
                placeholder={tasksLoading ? "Loading…" : "—"}
                // Regular tasks, then the client's open Other tasks. Values are
                // "t:<id>" / "o:<id>" so the two id sequences cannot collide;
                // WeekView turns the pick back into task / other_task.
                value={pickerValue(entry)}
                options={options}
                onChange={(v) => onChange("task", v || null)}
                emptyText={entry.client ? "No match" : "Pick a client first"}
                onCreate={onCreateTask && entry.client ? onCreateTask : undefined}
                createLabel={(q) => (q ? `+ New task "${q}"` : "+ New task")} />
            </div>
            {rolled && (
              <span className="te-rolled" role="img" aria-label="Rolled forward"
                title={rolledTitle}
                style={{ color: "#dc2626", fontWeight: 700, cursor: "help", flex: "none" }}>
                ⓘ
              </span>
            )}
          </div>
        )}
      </td>
      <td className="te-cell">
        {/* skipTab even when nothing is dictating the year: this column is
            derived in every case that matters -- from the task, or from the
            client and date -- so stopping in it on the way across a line is
            asking for an answer the grid already has. Still reachable by
            click and by the arrow keys, which is what keeps a task-less line
            settable. */}
        <ComboField ariaLabel="Year End" disabled={!editable} value={entry.year_end}
          options={YEARS.map((y) => ({ value: y, label: String(y) }))}
          onChange={(v) => onChange("year_end", v || null)}
          skipTab
          locked={yearLocked}
          lockedTitle={yearLocked
            ? `From ${pickedTask?.name || entry.task_name || "the task"} — change the task to change the year`
            : undefined} />
      </td>
      <td className="te-cell">
        <ComboField ariaLabel="Type" disabled={!editable}
          value={entry.billable_code || "BILLABLE"} options={TYPES}
          onChange={(v) => onChange("billable_code", v)} />
      </td>
      <td className="te-cell te-num-cell">
        <input className="te-in te-num" type="number" step="0.25" min="0"
          aria-label="Hrs" disabled={!editable}
          value={entry.hours ?? ""} placeholder="0"
          onFocus={selectHours.onFocus} onMouseUp={selectHours.onMouseUp}
          onKeyDown={selectHours.onKeyDown} data-select-on-focus="hours"
          onChange={(e) => onChange("hours", e.target.value)}
          onBlur={onHoursBlur} />
      </td>
      <td className="te-cell">
        {/* No `title` here: it would win over the row's tooltip on the very
            cell whose text is most often cut off. The summary carries the
            notes in full anyway. */}
        <input className="te-in" aria-label="Description" disabled={!editable}
          value={entry.notes || ""} placeholder="Description"
          onChange={(e) => onChange("notes", e.target.value)} />
      </td>
      <td className="te-cell te-status">
        {entry.status && entry.status !== "draft" ? (
          <div style={{ display: "grid", gap: 2 }}>
            <StatusChip status={entry.status} note={entry.review_note} />
            {entry.status === "rejected" && entry.review_note && (
              <span title={entry.review_note} style={{
                fontSize: 11, color: "#991b1b", whiteSpace: "nowrap",
                overflow: "hidden", textOverflow: "ellipsis", maxWidth: 140,
              }}>
                {entry.review_note}
              </span>
            )}
          </div>
        ) : (
          <span className="te-save">
            {saveStatus === "saving" ? "saving…" : saveStatus === "saved" ? "saved"
              : saveStatus === "error" ? "retry" : ""}
          </span>
        )}
      </td>
      <td className="te-cell te-del">
        {review
          ? (entry.id ? (
              <button className="te-sb" title="Send back for changes"
                onClick={() => onSendBack(entry)}>Send back</button>
            ) : null)
          : (editable ? (
              <button className="te-x" title="Delete line" onClick={onDelete}>✕</button>
            ) : null)}
      </td>
    </tr>
  );
}
